import { useEffect, useState } from "react";
import { DEFAULT_HOTLINE } from "@base/modules/appointment/appointmentDetailRules";
import commonService from "@base/modules/common/commonService";

interface ICommonSetting {
  key: string;
  description?: string;
  article_link?: string;
}

// The list is public and rarely changes, so one request serves the whole session.
let cached: Promise<ICommonSetting[]> | undefined;

const loadSettings = () => {
  cached ??= commonService
    .getListShow()
    .then((response) => (Array.isArray(response.data) ? (response.data as ICommonSetting[]) : []))
    .catch(() => {
      cached = undefined;
      return [];
    });

  return cached;
};

/**
 * Values from the shared settings list (`GET /utils/common/list`): the hotline, the contact
 * channels and the default transfer account.
 */
const useCommonSettings = () => {
  const [settings, setSettings] = useState<ICommonSetting[]>([]);

  useEffect(() => {
    let ignore = false;

    loadSettings().then((list) => !ignore && setSettings(list));

    return () => {
      ignore = true;
    };
  }, []);

  const find = (key: string) => settings.find((item) => item.key === key);
  const link = (key: string) => find(key)?.article_link?.trim() || "";
  const description = (key: string) => find(key)?.description?.trim() || "";

  return {
    hotline: link("APP_HOTLINE") || DEFAULT_HOTLINE,
    facebook: link("APP_FACEBOOK"),
    zalo: link("APP_ZALO"),
    email: link("APP_EMAIL"),
    /** The default transfer account, used when an order has no virtual account of its own. */
    bank: {
      name: description("BANK_NAME"),
      number: description("BANK_NUMBER"),
      receiver: description("BANK_RECEIVER"),
      qr: link("BANK_RECEIVER_QR"),
    },
    hasBankAccount: !!description("BANK_NAME") && !!description("BANK_NUMBER") && !!description("BANK_RECEIVER"),
  };
};

export default useCommonSettings;
